import React, { useState, useEffect, useMemo } from "react";
import { CircularProgress } from "@mui/material";
import Swal from "sweetalert2";
import DynamicForm from "../../../../data/Axios/DynamicForm";
import { HRService, setupManagementUrl } from "../../../../config";
import {
  allLeaveTypes,
  allPoliciesView,
} from "../../../../data/Queries/HRmanagement/leavemanagement";
import {
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../../data/Axios/DynamicService";
import { leavePolicyFields } from "../../../../data/DynamicTable/FormsFields/LeaveApplicationForms";

const base_url = HRService.uri;

const PolicyUpdateForm = (props) => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [policies, setPolicies] = useState([]); // used to lookup maxDays
  const [loading, setLoading] = useState(true);

  const initialValues = {
    leaveTypeId: props.data ? props.data.leaveTypeId : "",
    gender: props.data ? props.data.gender : "",
    acrues: props.data ? props.data.acrues : false,
    accrualRate: props.data ? props.data.accrualRate : "",
    accrualDate: props.data ? props.data.accrualDate : "",
    canCarryForward: props.data ? props.data.canCarryForward : false,
    maxDaysCarryForward: props.data ? props.data.maxDaysCarryForward : "",
    maxOpenApplications: props.data ? props.data.maxOpenApplications : "",
    maxDays: props.data ? props.data.maxDays : "",
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const [types, policies] = await Promise.all([
          graphqlQuery(allLeaveTypes, setupManagementUrl.uri),
          graphqlQuery(allPoliciesView, base_url),
        ]);
        if (types) setLeaveTypes(types);
        if (policies) setPolicies(policies);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const steps = useMemo(() => {
    return [
      {
        stepLabel: "Basic Information",
        stepDescription: "Specify applicable leave type and gender",
        icon: "assignment_ind",
        fields: ["leaveTypeId", "gender"],
      },
      {
        stepLabel: "Accrual Settings",
        stepDescription: "Configure leave accrual options and rates",
        icon: "schedule",
        fields: ["acrues", "accrualRate", "accrualDate"],
      },
      {
        stepLabel: "Carry Forward Rules",
        stepDescription: "Define carry-forward policy for unused leave",
        icon: "autorenew",
        fields: ["canCarryForward", "maxDaysCarryForward", "maxOpenApplications"],
      },
      {
        stepLabel: "Application Limits",
        stepDescription: "Set limits for leave applications",
        icon: "rule",
        fields: ["maxDays"],
      },
    ];
  }, []);

  const LeaveTypeOptions = leaveTypes
    ? leaveTypes.map((type) => ({
      value: type.id,
      label: type.type,
    }))
    : [];

  const fields = useMemo(() => {
    // Build leaveTypeId field with onChange to auto-populate maxDays
    const leaveTypeField = {
      name: "leaveTypeId",
      label: "Leave Type",
      type: "select",
      options: leaveTypes.map((type) => ({
        value: type.id,
        label: type.type,
      })),
      gridColumn: "span 2",
      required: true,
      onChange: (value, values, setFieldValue) => {
        const policyMatch = policies.find((p) => p.leaveTypeId === value);
        if (policyMatch && policyMatch.maxDays != null) {
          setFieldValue("maxDays", policyMatch.maxDays);
        } else {
          setFieldValue("maxDays", "");
        }
      },
    };

    // Replace or inject leaveTypeId into leavePolicyFields safely
    const otherFields = leavePolicyFields.filter(
      (f) => f.name !== "leaveTypeId" && f.name !== "maxDays"
    );

    const maxDaysField = {
      name: "maxDays",
      label: "Max Days",
      type: "number",
      gridColumn: "span 2",
      required: true,
    };

    return [leaveTypeField, ...otherFields, maxDaysField];
  }, [leaveTypes, policies]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const {
        leaveTypeId,


        accrualDate,

        maxDaysCarryForward,
        maxOpenApplications,
        maxDays,
      } = values;
      const acrues = values.acrues === true ? 1 : 0,
        canCarryForward = values.canCarryForward === true ? 1 : 0;
      const accrualRate = parseFloat(values.accrualRate);
      const mutationData = {
        id: props.data.id,
        isActive: 1,
        leaveTypeId,
        gender: values.gender ?? null,
        acrues,
        accrualRate,
        accrualDate,
        canCarryForward,
        maxDaysCarryForward,
        maxOpenApplications,
        maxDays,
      };
      const response = await generateAndExecuteMutation(
        "updatePolicy",
        mutationData,
        "leavePolicy",
        base_url
      );
      if (Object.values(response)[0] != null) {
        props.onClose();
        props.onAction();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Policy updated successfully.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }

    setSubmitting(false);
  };

  return loading ? (
    <CircularProgress />
  ) : (
    <DynamicForm
      initialValues={initialValues}
      fields={fields}
      steps={steps}
      onSubmit={handleSubmit}
      title="Update Policy"
      subtitle="Fill out the form below to update the policy."
    />
  );
};

export default PolicyUpdateForm;
